import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { push } from '@lagunovsky/redux-react-router'
import moment from 'moment'
import i18n from 'simple-react-i18n'
import { find, orderBy } from 'lodash'
import { Grid } from '@mui/material'
import ProfileCardList from '../../../../components/list/cardList/ProfileCardList'
import UserAction from '../actions/UserAction'
import { getProfileCard, getSetting } from '../../../../utils/SettingUtils'
import { getFullDateMini } from 'utils/DateUtil'
import Checkbox from '../../../../components/forms/Checkbox'
import { getI18nTitleData, normalizeString } from '../../../../utils/StringUtil'
import HomeAction from 'home/actions/HomeAction'
import CityAction from '../../../../referencial/components/city/actions/CityAction'
import AddUserModal from './AddUserModal'
import ProgressCard from 'components/card/ProgressCard'
import WaitAction from 'wait/WaitAction'
import FormFilterUsers from './FormFilterUsers'
import { getLocalStorageJson } from 'utils/FormUtils'
import { emptyFilterUsers, FILTER_ADMIN_SEARCH } from '../constants/UserConstants'
import SimpleKeyFigurePanel from '../../../../components/keyFigures/SimpleKeyFigurePanel'
import AdministrationAction from 'administration/actions/AdministrationAction'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'

const checkBlockUser = (user) => {
    if (user.endDate) {
        const format = 'DD/MM/YYYY'
        const formatEnDate = moment(moment(user.endDate).format(format), format)
        const formatNowDate = moment(moment().format(format), format)
        return formatEnDate.isSameOrAfter(formatNowDate) ? 0 : 1
    } else if (user.resetPassword) {
        return 2
    }
    return 0
}

const getHash = (user) => normalizeString(user.headers.map((key) => user[key]))

const getFilteredData = (data, filters) => {
    const search = normalizeString(filters.searchValue || '')
    return data.filter((i) => getHash(i).includes(search))
}

const UsersApp = () => {
    const dispatch = useDispatch()

    const {
        users,
        applicativeUsers,
        contacts,
        contributors,
        applicationSettings,
        cities,
        accountUser,
    } = useSelector((store) => ({
        users: store.UserReducer.users,
        applicativeUsers: store.UserReducer.applicativeUsers,
        contacts: store.ContactReducer.contacts,
        contributors: store.ContributorReducer.contributors,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        cities: store.CityReducer.cities,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const [filter, setFilter] = useState(getLocalStorageJson(FILTER_ADMIN_SEARCH) || emptyFilterUsers)
    const [isOpen, setIsOpen] = useState(false)
    const [dontSendMail, setDontSendMail] = useState(false)
    const [pictures, setPictures] = useState({})
    const [dataLoaded, setDataLoaded] = useState(false)
    const [dataApplicativeLoaded, setDataApplicativeLoaded] = useState(false)

    // Lecture de la dernière valeur des photos sans en faire une dépendance des calculs
    // (sinon le fetch des photos relancerait en boucle le formatage des listes).
    const picturesRef = useRef(pictures)
    picturesRef.current = pictures

    const changeUser = useCallback((login) => {
        dispatch(push(`/administration/user/${login}`))
    }, [dispatch])

    const exportUsers = useCallback(() => {
        const data = orderBy(users, ['login'], ['asc']).reduce((acc, user) => {
            const index = checkBlockUser(user)
            const contact = find(contacts, (o) => o.code == user.contactCode)
            const contributor = find(contributors, (o) => o.code == user.contributorCode)

            const showedAdmin = user.admin === '1' ? i18n.admin : null
            const showedBlocked = user.consultant ? i18n.consultant : null
            const showedMetadata = user.metadata === '1' ? i18n.referencialManager : null
            const nbMaxConnection = getSetting(applicationSettings, 'maximumNumberTry')
            const blockedUser =
                nbMaxConnection && user.connexionAttempts >= nbMaxConnection
                    ? i18n.blockedUser
                    : null
            const showedLabo = user.labo === '1' ? i18n.technicien : null
            const showedConsulting =
                (!user.admin || user.admin == '0') &&
                    (!user.labo || user.labo == '0') &&
                    (!user.metadata || user.metadata == '0')
                    ? i18n.consultant
                    : null
            const habilitation = [
                showedAdmin,
                showedBlocked,
                showedMetadata,
                showedLabo,
                blockedUser,
                showedConsulting,
            ].filter((i) => !!i)

            const result = {
                name: contact ? contact.name : '',
                login: user.login,
                email: contact ? contact.email : '',
                beginDate: user.beginDate ? getFullDateMini(user.beginDate) : '',
                endDate: user.endDate ? getFullDateMini(user.endDate) : '',
                contributor: contributor ? contributor.name : '',
                lastConnection: user.lastConnexion ? getFullDateMini(user.lastConnexion) : '',
                access: habilitation.join(', '),
                state:
                    index === 0
                        ? i18n.users
                        : index === 1
                            ? i18n.disableUsers
                            : i18n.activationProgress,
            }
            acc.push(result)
            return acc
        }, [])
        if (data.length) {
            data[0].headers = [
                'login',
                'name',
                'email',
                'beginDate',
                'endDate',
                'lastConnection',
                'contributor',
                'access',
                'state',
            ]
        }
        return data
    }, [users, contacts, contributors, applicationSettings])

    const formatUsersForTables = useCallback((usersToFormat) => {
        return orderBy(usersToFormat, ['login'], ['asc']).map((user) => {
            const contact = find(contacts, (o) => parseInt(o.code) === parseInt(user.contactCode))
            const contributor = user?.contributorCode && contributors.find(o => parseInt(o.code) === parseInt(user.contributorCode))
            const town = user?.townCode && cities.find(c => parseInt(c.code) === parseInt(user.townCode))
            const name = contact?.name || ''
            const mail = contact?.email || ''
            const mobile = contact?.mobile || ''
            const tel = contact?.phoneTel || ''
            const contributorName = contributor?.name || ''
            const townName = town?.name || ''
            return {
                ...user,
                name,
                lastConnexion: { value: getFullDateMini(user.lastConnexion), sortValue: user.lastConnexion },
                mail,
                mobile,
                tel,
                organism: contributorName,
                townName,
                admin: <Checkbox checked={user.admin === '1'} disabled={true} />,
                metadata: <Checkbox checked={user.metadata === '1'} disabled={true} />,
            }
        })
    }, [contacts, contributors, cities])

    const getCardListFormatted = useCallback((usersToFormat) => {
        return orderBy(usersToFormat, ['login'], ['asc']).map((user) => {
            const contact = find(contacts, (o) => o.code == user.contactCode)
            const contributor = find(contributors, (o) => o.code == user.contributorCode)
            const name = contact ? contact.name : ''
            const tooltipeContributor =
                    contributor && contributor.name
                        ? { className: 'tooltipped truncate', 'data-tooltip': contributor.name }
                        : { className: 'truncate' }
            const mnemonique =
                    contributor && (contributor.mnemonique || contributor.name) ? (
                        <span {...tooltipeContributor}>
                            {contributor.mnemonique ? contributor.mnemonique : contributor.name}
                        </span>
                    ) : null
            const picture = picturesRef.current[user.login] ? picturesRef.current[user.login] : ''
            const result = {
                ...user,
                name,
                mnemonique,
                picture,
            }
            return {
                content: getProfileCard(
                    result,
                    getSetting(applicationSettings, 'maximumNumberTry') || 5,
                ),
                onClick: () => changeUser(result.login),
                login: result.login,
            }
        })
    }, [contacts, contributors, applicationSettings, changeUser])

    const {
        allUsersFiltered,
        activateUsers,
        disableUsers,
        inActivationsUsers,
    } = useMemo(() => {
        const { contributors: filterContributors, cities: filterCities, isAdmin, metadata, labo, consultant } = filter

        const usersFilteredByOrganization = filterContributors?.length > 0 ? users.filter(user => filterContributors.includes(user.contributorCode)) : users
        const usersFilteredByCity = filterCities?.length > 0 ? usersFilteredByOrganization.filter(user => {
            const contact = find(contacts, (o) => o.code == user.contactCode)
            return (contact && filterCities.includes(contact.cityCode)) && user
        }) : usersFilteredByOrganization

        const usersFilteredByAdmin = isAdmin ? usersFilteredByCity.filter(user => user.admin === '1') : usersFilteredByCity
        const usersFilteredByAdministrator = metadata ? usersFilteredByAdmin.filter(user => user.metadata === '1') : usersFilteredByAdmin
        const usersFilteredByTechnicien = labo ? usersFilteredByAdministrator.filter(user => user.labo === '1') : usersFilteredByAdministrator
        const usersFilteredByConsultant = consultant ? usersFilteredByTechnicien.filter(user => user.consultant === '1') : usersFilteredByTechnicien

        const usersWithContactData = formatUsersForTables(usersFilteredByConsultant)

        const dataActivateUsers = getFilteredData(usersWithContactData.filter((u) => checkBlockUser(u) === 0), filter)
        const dataDisableUsers = getFilteredData(usersWithContactData.filter((u) => checkBlockUser(u) === 1), filter)
        const dataInActivationsUsers = getFilteredData(usersWithContactData.filter((u) => checkBlockUser(u) === 2), filter)

        return {
            allUsersFiltered: usersFilteredByConsultant,
            activateUsers: dataActivateUsers,
            disableUsers: dataDisableUsers,
            inActivationsUsers: dataInActivationsUsers,
        }
    }, [users, contacts, formatUsersForTables, filter])

    const getPicture = (login) => {
        dispatch(AdministrationAction.fetchProfilePicture(login)).then(res => {
            const extension = res.name.substring(res.name.lastIndexOf('.'), res.name.length)
            if (extension.length > 0) {
                const namePic = `${login}${extension}`
                dispatch(AdministrationAction.fetchPictureWithoutCache(namePic)).then(urlPic => {
                    setPictures((prev) => ({ ...prev, [login]: urlPic }))
                })
            }
        })
    }

    useEffect(() => {
        if (activateUsers.length <= 25) activateUsers.map(u => getPicture(u.login))
        if (disableUsers.length <= 25) disableUsers.map(u => getPicture(u.login))
        if (inActivationsUsers.length <= 25) inActivationsUsers.map(u => getPicture(u.login))
    }, [activateUsers, disableUsers, inActivationsUsers])

    const formattedActivateUsers = useMemo(() => activateUsers.length > 25 ? activateUsers : getCardListFormatted(activateUsers), [activateUsers, getCardListFormatted, pictures])
    const formattedDisableUsers = useMemo(() => disableUsers.length > 25 ? disableUsers : getCardListFormatted(disableUsers), [disableUsers, getCardListFormatted, pictures])
    const formattedInActivationsUsers = useMemo(() => inActivationsUsers.length > 25 ? inActivationsUsers : getCardListFormatted(inActivationsUsers), [inActivationsUsers, getCardListFormatted, pictures])

    const formattedApplicativeUsers = useMemo(() => {
        const { contributors: filterContributors, cities: filterCities, isAdmin, metadata, labo, consultant } = filter

        const usersFilteredByOrganization = filterContributors?.length > 0 ? applicativeUsers.filter(user => filterContributors.includes(user.contributorCode)) : applicativeUsers
        const usersFilteredByCity = filterCities?.length > 0 ? usersFilteredByOrganization.filter(user => {
            const contact = find(contacts, (o) => o.code == user.contactCode)
            return (contact && filterCities.includes(contact.cityCode)) && user
        }) : usersFilteredByOrganization

        const usersFilteredByAdmin = isAdmin ? usersFilteredByCity.filter(user => user.admin === '1') : usersFilteredByCity
        const usersFilteredByAdministrator = metadata ? usersFilteredByAdmin.filter(user => user.metadata === '1') : usersFilteredByAdmin
        const usersFilteredByTechnicien = labo ? usersFilteredByAdministrator.filter(user => user.labo === '1') : usersFilteredByAdministrator
        const usersFilteredByConsultant = consultant ? usersFilteredByTechnicien.filter(user => user.consultant === '1') : usersFilteredByTechnicien

        const filteredApplicativeUsers = getFilteredData(usersFilteredByConsultant, filter)

        return filteredApplicativeUsers.length > 25 ? filteredApplicativeUsers : getCardListFormatted(filteredApplicativeUsers)
    }, [applicativeUsers, contacts, getCardListFormatted, filter])

    const onAddUserPopup = useCallback(() => setIsOpen(true), [])
    const onCloseAddUserModal = useCallback(() => setIsOpen(false), [])

    const addUser = useCallback((user) => {
        dispatch(UserAction.addUser(user, dontSendMail)).then(() => {
            dispatch(WaitAction.waitStart())
            dispatch(UserAction.fetchUsers()).then(() => {
                dispatch(WaitAction.waitStop())
                dispatch(push(`/administration/user/${user.login}`))
            })
        })
    }, [dispatch, dontSendMail])

    useTitle(() => [{
        title: i18n.administration,
        href: 'administration',
    }, {
        title: i18n.users,
        href: 'administration/user',
    }], [])

    useActions(() => {
        const actions = {
            export: () => ({
                data: exportUsers(),
                exportType: 'xlsx',
                titleFile: i18n.users,
            }),
        }
        if (!(accountUser.consultant === '1')) {
            return {
                ...actions,
                new: () => onAddUserPopup(),
            }
        }
        return actions
    }, [exportUsers, onAddUserPopup, users, accountUser])

    // Chargement initial des données (équivalent componentDidMount).
    useEffect(() => {
        dispatch(HomeAction.setHelpLink('administration', '92'))
        dispatch(HomeAction.updateGlobalResearch(''))
        if (!cities.length) {
            dispatch(CityAction.fetchCities())
        }
        if (!accountUser?.login) {
            dispatch(UserAction.fetchUser(localStorage.getItem('SIEAU_LOGIN')))
        }
        // Le formatage se fait dans les effets ci-dessous quand les données arrivent dans le store ;
        // le .then ne couvre que le cas où le fetch renvoie une liste vide (pour lever le loader).
        if (!users.length) {
            dispatch(UserAction.fetchUsers()).then(() => setDataLoaded(true))
        } else setDataLoaded(true)
        if (!applicativeUsers.length) {
            dispatch(UserAction.fetchApplicativeUsers()).then(() => setDataApplicativeLoaded(true))
        } else setDataApplicativeLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const keyFigureData = [{
        title: 'users',
        value: allUsersFiltered.length,
    }, {
        title: 'admins',
        value: allUsersFiltered.filter((user) => user.admin).length,
    }]

    return (
        <Grid container spacing={1}>
            <Grid size={10} container direction='column' spacing={1}>
                <FormFilterUsers onValidate={setFilter}/>
                {dataLoaded && dataApplicativeLoaded ? (
                    <>
                        {!filter.applicativeUsers ? (
                            <>
                                <ProfileCardList
                                    title={`${i18n.users} : ${formattedActivateUsers.length} ${getI18nTitleData(i18n.element, i18n.elements, formattedActivateUsers)}`}
                                    users={formattedActivateUsers}
                                    onClickTable={changeUser}
                                    open
                                />
                                <ProfileCardList
                                    title={`${i18n.activationProgress} : ${formattedInActivationsUsers.length} ${getI18nTitleData(i18n.element, i18n.elements, formattedInActivationsUsers)}`}
                                    users={formattedInActivationsUsers}
                                    onClickTable={changeUser}
                                />
                                <ProfileCardList
                                    title={`${i18n.disableUsers} : ${formattedDisableUsers.length} ${getI18nTitleData(i18n.element, i18n.elements, formattedDisableUsers)}`}
                                    users={formattedDisableUsers}
                                    onClickTable={changeUser}
                                />
                            </>
                        ) : (
                            <ProfileCardList
                                title={`${i18n.usersApplication} : ${formattedApplicativeUsers.length} ${getI18nTitleData(i18n.element, i18n.elements, formattedApplicativeUsers)}`}
                                users={formattedApplicativeUsers}
                                onClickTable={changeUser}
                            />
                        )}
                    </>
                ) : <ProgressCard indeterminate round /> }
            </Grid>
            <Grid size={2}>
                <SimpleKeyFigurePanel data={keyFigureData} />
            </Grid>
            <AddUserModal
                addUser={addUser}
                dontSendMail={dontSendMail}
                onChangeSendMail={(v) => setDontSendMail(v)}
                isOpen={isOpen}
                onClose={onCloseAddUserModal}
            />
        </Grid>
    )
}

export default UsersApp
